import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import type { Shop } from '@notify/shared';
import { RushMode } from '@notify/shared';

import { useAuth } from '../../hooks/useAuth';
import { shopService } from '../../services/shop.service';

// ─── Icons ────────────────────────────────────────────────────────────────────

function BellIcon() {
  return (
    <svg className="h-6 w-6 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 22a2 2 0 001.995-1.85L14 20H10a2 2 0 001.995 1.85L12 22zM12 2a7 7 0 016.996 6.76L19 9v5l2 3v1H3v-1l2-3V9a7 7 0 017-7z" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <line x1="8" y1="6" x2="21" y2="6" strokeLinecap="round" />
      <line x1="8" y1="12" x2="21" y2="12" strokeLinecap="round" />
      <line x1="8" y1="18" x2="21" y2="18" strokeLinecap="round" />
      <circle cx="3" cy="6" r="1" fill="currentColor" stroke="none" />
      <circle cx="3" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="3" cy="18" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function LayersIcon() {
  return (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function HamburgerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// ─── Rush mode badge ──────────────────────────────────────────────────────────

function RushBadge({ mode }: { mode: RushMode | null }) {
  if (!mode) return null;
  const styles: Record<RushMode, string> = {
    [RushMode.NORMAL]: 'bg-gray-100 text-gray-600',
    [RushMode.BUSY]: 'bg-amber-100 text-amber-700',
    [RushMode.HEAVY_RUSH]: 'bg-red-100 text-red-600',
  };
  const labels: Record<RushMode, string> = {
    [RushMode.NORMAL]: 'Normal',
    [RushMode.BUSY]: 'Busy',
    [RushMode.HEAVY_RUSH]: 'Heavy Rush',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${styles[mode]}`}>
      {labels[mode]}
    </span>
  );
}

// ─── Nav config ───────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { label: 'Dashboard', to: '/dashboard', Icon: GridIcon },
  { label: 'Orders', to: '/orders', Icon: ListIcon },
  { label: 'Batches', to: '/batches', Icon: LayersIcon },
  { label: 'Settings', to: '/settings', Icon: GearIcon },
] as const;

const TITLE_MAP: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/orders': 'Orders',
  '/batches': 'Batches',
  '/settings': 'Settings',
};

// ─── Layout ───────────────────────────────────────────────────────────────────

export default function ShopLayout() {
  // Mini-sidebar: persisted preference
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('notify_sidebar_collapsed') === 'true';
  });

  // Mobile overlay: open/closed
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [shop, setShop] = useState<Shop | null>(null);
  const { user, logout } = useAuth();
  const location = useLocation();

  const pageTitle = TITLE_MAP[location.pathname] ?? 'Dashboard';
  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  useEffect(() => {
    shopService.getMyShop().then(setShop).catch(() => null);
  }, []);

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('notify_sidebar_collapsed', String(next));
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside
        className={`fixed top-0 left-0 z-50 flex h-screen flex-col bg-[#0F172A] transition-all duration-300 ease-in-out
          ${collapsed ? 'w-16' : 'w-60'}
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      >
        {/* Logo + shop name */}
        <div className="relative flex h-16 shrink-0 items-center border-b border-white/10 px-4">
          <span className="text-[#6366F1]">
            <BellIcon />
          </span>
          {!collapsed && (
            <div className="ml-2.5 min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-white">
                {shop?.name ?? 'My Shop'}
              </p>
              <p className="text-xs text-white/40">Notify</p>
            </div>
          )}

          {/* Collapse toggle — desktop only */}
          <button
            onClick={toggleCollapsed}
            className="absolute -right-3 top-1/2 hidden -translate-y-1/2 items-center justify-center rounded-full bg-[#6366F1] text-white shadow-md transition-colors hover:bg-indigo-500 md:flex"
            style={{ width: 24, height: 24 }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </button>

          {/* Mobile close button */}
          <button
            className="ml-auto text-white/60 hover:text-white md:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" />
              <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-4 space-y-1">
          {NAV_ITEMS.map(({ label, to, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end
              title={collapsed ? label : undefined}
              onClick={() => {
                if (window.innerWidth < 768) setSidebarOpen(false);
              }}
              className={({ isActive }) =>
                `flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors
                ${collapsed ? 'justify-center' : 'gap-3'}
                ${isActive
                  ? 'bg-[#6366F1] text-white'
                  : 'text-white/60 hover:bg-white/10 hover:text-white'
                }`
              }
            >
              <Icon />
              {!collapsed && <span className="truncate">{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom: user info + logout */}
        <div className="shrink-0 border-t border-white/10 p-3">
          {!collapsed && (
            <div className="mb-2 rounded-lg bg-white/5 px-3 py-2">
              <p className="truncate text-sm font-medium text-white">
                {user?.email.split('@')[0] ?? 'Owner'}
              </p>
              <p className="truncate text-xs text-white/50">{user?.email ?? ''}</p>
            </div>
          )}
          <button
            onClick={logout}
            title={collapsed ? 'Logout' : undefined}
            className={`flex w-full items-center rounded-lg px-3 py-2.5 text-sm font-medium text-white/60 transition-colors hover:bg-white/10 hover:text-white
              ${collapsed ? 'justify-center' : 'gap-3'}`}
          >
            <LogoutIcon />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* ── Main content ──────────────────────────────────────────────────────── */}
      <div
        className={`flex min-h-screen flex-col transition-all duration-300 ${
          collapsed ? 'md:ml-16' : 'md:ml-60'
        }`}
      >
        {/* Top header */}
        <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4 shadow-sm sm:px-6">
          {/* Hamburger — mobile only */}
          <button
            className="text-[#64748B] hover:text-[#0F172A] transition-colors md:hidden"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open sidebar"
          >
            <HamburgerIcon />
          </button>

          <div className="flex flex-1 items-center justify-between gap-4">
            <h1 className="text-lg font-semibold text-[#0F172A]">{pageTitle}</h1>
            <div className="flex items-center gap-3">
              <RushBadge mode={shop?.rushMode ?? null} />
              <p className="hidden text-sm text-[#64748B] sm:block">{today}</p>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
